'use client';

import type { AdminV2EntryAction } from '@/admin-v2/entry-actions/entry-action';
import { isAdminV2InternalEntryActionHref } from '@/admin-v2/entry-actions/entry-action-runtime';
import {
  resolveAdminV2PageButtonAction,
  resolveAdminV2PageButtonByRuntimeIdentity,
} from '@/admin-v2/pages/page-button-contract';
import { renderAdminV2WorkspaceActionConfirmationText } from '@/admin-v2/workspace/workspace-action-confirmation-text';
import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import { cmsComponentButtonInputRequiresWorkspace } from '@/cms/blocks/component-button-input';
import type { WorkspaceRuntimeDialogCallbacks } from '@/cms/components/blocks/workspace/workspace-1/contracts/dialog-callbacks';
import type { WorkspaceRuntimeActionCallbacks } from '@/cms/components/blocks/workspace/workspace-1/contracts/dialog-callbacks';
import type { WorkspaceTaskBlockPaymentContext } from '@/cms/components/blocks/workspace/workspace-1/contracts/task-block-payment';
import {
  type WorkspacePrefillRequestV1,
  createCmsWorkspaceLaunchId,
} from '@/cms/components/blocks/workspace/workspace-prefill-contract';
import {
  type CmsWorkspaceLaunchDetail,
  dispatchCmsWorkspaceLaunch,
  subscribeCmsWorkspaceActivation,
} from '@/cms/components/blocks/workspace/workspace-session-controller';
import { CmsComponentButtonActionProvider } from '@/cms/components/component-button-action';
import { CmsLoginWorkspacePreparingOverlay } from '@/cms/components/overlays/cms-login-workspace-preparing-overlay';
import { resolveWorkspaceLaunchToolId } from '@/cms/workspace/workspace-launch-tool';
import { X } from '@/components/icons/app-icons';
import { UpgradeDialogActionProvider } from '@/components/paid-access/upgrade-dialog-action';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/overlay/alert-dialog';
import {
  type AuthSessionTransitionToken,
  beginAuthSessionTransition,
  cancelAuthSessionTransition,
  markAuthSessionTransitionValidating,
  useAuthSessionObserver,
} from '@/lib/auth/auth-session-observer';
import type { CredentialRuntimeConfig } from '@/lib/auth/credential-runtime-config';
import { toast } from '@/lib/ui/toast';
import type { CheckoutErrorCode } from '@/payment/services/checkout/types';
import {
  type AdminV2LoginPopupSceneSlug,
  adminV2DefaultLoginPopupSceneSlug,
  parseAdminV2LoginPopupSceneSlug,
} from '@/site-runtime/code-owned/login-popup-scenes';
import {
  type AdminV2PaywallPopupBranchSlug,
  parseAdminV2PaywallPopupBranchSlug,
  resolveAdminV2PaywallBranchSlugFromUpgradeReason,
} from '@/site-runtime/code-owned/paywall-branches';
import type { SiteRuntimeTarget } from '@/site-runtime/runtime-config-contract';
import { isWorkspaceCodeOwnedPagePath } from '@/workspace/code-owned/workspace-page-contract';
import {
  type ComponentType,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  cancelSiteRuntimeWorkspaceAuthIntent,
  clearSiteRuntimeWorkspaceAuthIntentDocumentHint,
  consumeSiteRuntimeWorkspaceAuthIntent,
  hasSiteRuntimeWorkspaceAuthIntentDocumentHint,
  issueSiteRuntimeWorkspaceAuthIntent,
  showSiteRuntimeWorkspaceAuthIntentDocumentHint,
} from '../auth/site-runtime-workspace-auth-intent';
import {
  type SiteRuntimeCheckoutLoginIntent,
  readSiteRuntimeCheckoutLoginIntent,
} from '../checkout/site-runtime-checkout-login-intent';
import { SiteRuntimeCheckoutReturnPathTracker } from '../checkout/site-runtime-checkout-return-path';
import { useSiteRuntimeHostedCheckout } from '../checkout/use-site-runtime-hosted-checkout';
import { getSiteRuntimeLinkRel, localizeSiteRuntimeHref } from '../link-href';
import { buildSiteRuntimeLoginDialogModel } from '../login-popup-model';
import type { SiteRuntimePageWorkspaceHero } from '../page-workspace-presentation';
import { readSiteRuntimeCheckoutErrorCopy } from '../pricing/site-runtime-checkout-errors';
import { resolveSiteRuntimeCheckoutIntentDisplay } from '../pricing/site-runtime-checkout-intent';
import type {
  SiteRuntimeGlobalSnapshot,
  SiteRuntimePage,
  SiteRuntimePageButton,
} from '../types';
import {
  type SiteRuntimeWorkspaceTransition,
  getSiteRuntimeWorkspaceMountDetail,
  idleSiteRuntimeWorkspaceTransition,
  isSiteRuntimeWorkspaceTransitionReady,
  reduceSiteRuntimeWorkspaceTransition,
  shouldMountSiteRuntimeWorkspace,
} from '../workspace/site-runtime-workspace-transition';
import { SiteRuntimePageBoundWorkspaceHost } from './site-runtime-page-bound-workspace-host';
import {
  type SiteRuntimeLoginDialogRuntimeProps,
  type SiteRuntimePaywallDialogRuntimeProps,
  type SiteRuntimeTaskPaymentDialogRuntimeProps,
  loadSiteRuntimeLoginDialogRuntime,
  loadSiteRuntimePaywallDialogRuntime,
  loadSiteRuntimeTaskPaymentDialogRuntime,
  preloadSiteRuntimePaywallDialogRuntime,
  readLoadedSiteRuntimeLoginDialogRuntime,
  readLoadedSiteRuntimePaywallDialogRuntime,
  readLoadedSiteRuntimeTaskPaymentDialogRuntime,
} from './site-runtime-popup-runtime-loader';
import { SiteRuntimeTaskPaymentDialogShell } from './site-runtime-task-payment-dialog-shell';

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

type ActionExecutionOptions = {
  workspacePrefill?: WorkspacePrefillRequestV1;
};

export type SiteRuntimeLoginRequest =
  | {
      mode: 'plain';
      scene: AdminV2LoginPopupSceneSlug;
      onCancel?: () => void;
    }
  | {
      mode: 'workspace';
      scene: AdminV2LoginPopupSceneSlug;
      status?: 'preparingWorkspace' | 'signInFailed';
    }
  | {
      callbackUrl: string;
      mode: 'checkout';
      scene: 'checkout';
    };

export type SiteRuntimeElementDataAttributes = Readonly<Record<string, string>>;

export type SiteRuntimePopupPreviewEditTargets = {
  branchBody?: SiteRuntimeElementDataAttributes;
  branchPrimaryButton?: SiteRuntimeElementDataAttributes;
  branchTitle?: SiteRuntimeElementDataAttributes;
  marketingSlideBody?: (
    slideIndex: number
  ) => SiteRuntimeElementDataAttributes | undefined;
  marketingSlideCta?: (
    slideIndex: number
  ) => SiteRuntimeElementDataAttributes | undefined;
  marketingSlideTitle?: (
    slideIndex: number
  ) => SiteRuntimeElementDataAttributes | undefined;
};

type ActiveDialog =
  | {
      kind: 'login';
      request: SiteRuntimeLoginRequest;
    }
  | {
      kind: 'paywall';
      branchSlug: AdminV2PaywallPopupBranchSlug;
      taskBlockPayment?: WorkspaceTaskBlockPaymentContext;
    }
  | {
      kind: 'task-payment';
      taskBlockPayment: WorkspaceTaskBlockPaymentContext;
    }
  | { kind: 'marketing'; popupId: string; branchSlug: string }
  | {
      kind: 'checkout';
      checkoutIntentKey: string;
      checkoutSurface: 'modal' | 'side_panel';
      planName: string;
      buttonLabel: string;
    }
  | {
      kind: 'workspace-action-confirmation';
      title: string;
      description: string;
      confirmLabel: string;
      cancelLabel: string;
      onConfirm: () => void | Promise<void>;
      onCancel: () => void;
    };

type SiteRuntimeActionContextValue = {
  beginWorkspaceAuthReturn: (
    callbackUrl: string,
    loginScene: AdminV2LoginPopupSceneSlug
  ) => Promise<boolean>;
  cancelWorkspaceAuthReturn: () => Promise<void>;
  closeDialog: () => void;
  executeAction: (
    action: AdminV2EntryAction,
    options?: ActionExecutionOptions
  ) => void;
  executeTarget: (target: SiteRuntimeTarget) => void;
  executeWorkspaceAction: NonNullable<
    WorkspaceRuntimeActionCallbacks['onExecuteEntryAction']
  >;
  credentialCapabilities: CredentialRuntimeConfig;
  getActionHref: (action: AdminV2EntryAction) => string | null;
  getTargetHref: (target: SiteRuntimeTarget) => string | null;
  global: SiteRuntimeGlobalSnapshot;
  isTaskBlockRetentionSuppressed: () => boolean;
  locale: string;
  openLogin: (request: SiteRuntimeLoginRequest) => void;
  openPaywallBranch: (
    branchSlug: AdminV2PaywallPopupBranchSlug,
    taskBlockPayment?: WorkspaceTaskBlockPaymentContext
  ) => void;
  openTaskBlockPayment: (
    taskBlockPayment: WorkspaceTaskBlockPaymentContext
  ) => void;
  page: SiteRuntimePage | null;
  preloadAction: (action: AdminV2EntryAction) => void;
  preloadTarget: (target: SiteRuntimeTarget) => void;
  reportWorkspaceShellReady: () => void;
  resumeWorkspaceAuthReturn: () => void;
  reportRuntimeError: (message: string) => void;
  registerWorkspaceToolSelector: NonNullable<
    WorkspaceRuntimeActionCallbacks['registerToolSelector']
  >;
  resolveButtonAction: (
    button: SiteRuntimePageButton
  ) => AdminV2EntryAction | null;
  activeDialog: ActiveDialog | null;
  suppressWorkspaceAutoLaunch: boolean;
  suppressTaskBlockRetention: () => void;
  workspaceTransition: SiteRuntimeWorkspaceTransition;
};

const SiteRuntimeActionContext =
  createContext<SiteRuntimeActionContextValue | null>(null);

export function useSiteRuntimeActionContext() {
  const context = useContext(SiteRuntimeActionContext);
  if (!context) {
    throw new Error('Site runtime action context is missing.');
  }

  return context;
}

export function useSiteRuntimeWorkspaceDialogCallbacks() {
  const { openLogin, openPaywallBranch } = useSiteRuntimeActionContext();

  return useMemo<WorkspaceRuntimeDialogCallbacks>(
    () => ({
      onCreditsInsufficient: (taskBlockPayment) =>
        openPaywallBranch('creditsInsufficient', taskBlockPayment),
      onLoginRequired: (scene, onCancel) =>
        openLogin(
          onCancel
            ? { mode: 'plain', scene, onCancel }
            : { mode: 'workspace', scene }
        ),
      onUpgradeRequired: (reason, taskBlockPayment) =>
        openPaywallBranch(
          resolveAdminV2PaywallBranchSlugFromUpgradeReason(reason),
          taskBlockPayment
        ),
    }),
    [openLogin, openPaywallBranch]
  );
}

export function useSiteRuntimeWorkspaceActionCallbacks() {
  const { executeWorkspaceAction, registerWorkspaceToolSelector } =
    useSiteRuntimeActionContext();

  return useMemo<WorkspaceRuntimeActionCallbacks>(
    () => ({
      onExecuteEntryAction: executeWorkspaceAction,
      registerToolSelector: registerWorkspaceToolSelector,
    }),
    [executeWorkspaceAction, registerWorkspaceToolSelector]
  );
}

function SiteRuntimeCheckoutLoginResumeController() {
  const [intent, setIntent] = useState<SiteRuntimeCheckoutLoginIntent | null>(
    null
  );

  useEffect(() => {
    setIntent(readSiteRuntimeCheckoutLoginIntent(window.location.href));
  }, []);

  return intent ? (
    <ActiveSiteRuntimeCheckoutLoginResume intent={intent} />
  ) : null;
}

function ActiveSiteRuntimeCheckoutLoginResume({
  intent,
}: {
  intent: SiteRuntimeCheckoutLoginIntent;
}) {
  const { global, locale, openLogin, reportRuntimeError } =
    useSiteRuntimeActionContext();
  const getCheckoutErrorMessage = useCallback(
    (errorCode: CheckoutErrorCode) =>
      readSiteRuntimeCheckoutErrorCopy(global.pricing, errorCode),
    [global.pricing]
  );
  const onCheckoutError = useCallback(
    (errorCode: CheckoutErrorCode) => {
      reportRuntimeError(getCheckoutErrorMessage(errorCode));
    },
    [getCheckoutErrorMessage, reportRuntimeError]
  );
  const onUnauthenticated = useCallback(
    (loginCallbackUrl: string) => {
      openLogin({
        callbackUrl: loginCallbackUrl,
        mode: 'checkout',
        scene: 'checkout',
      });
    },
    [openLogin]
  );
  useSiteRuntimeHostedCheckout({
    checkoutIntentKey: intent.checkoutIntentKey,
    checkoutSurface: intent.checkoutSurface,
    conversionId: intent.conversionId,
    getCheckoutErrorMessage,
    locale,
    onCheckoutError,
    onUnauthenticated,
    paywallBranchSlug: intent.paywallBranchSlug,
    resumeLoginIntent: true,
  });

  return null;
}

function scrollToWorkspaceMount() {
  window.setTimeout(() => {
    document
      .getElementById('site-runtime-workspace')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 0);
}

function SiteRuntimeWorkspaceAuthReturnController({
  enabled,
  onAuthenticated,
  onUnauthenticated,
}: {
  enabled: boolean;
  onAuthenticated: () => void;
  onUnauthenticated: () => void;
}) {
  const sessionSnapshot = useAuthSessionObserver();
  const observedValidationRef = useRef(false);
  const ownedTransitionRef = useRef<AuthSessionTransitionToken | null>(null);
  const transition = sessionSnapshot.transition;
  const hasActiveTransition =
    transition?.phase === 'pending' || transition?.phase === 'validating';
  const hasSignInTransition = transition?.direction === 'sign-in';

  useEffect(() => {
    if (!enabled) {
      if (ownedTransitionRef.current) {
        cancelAuthSessionTransition(ownedTransitionRef.current);
      }
      ownedTransitionRef.current = null;
      observedValidationRef.current = false;
      return;
    }

    if (transition?.direction === 'sign-in') {
      observedValidationRef.current = true;
      if (transition.phase === 'pending') {
        markAuthSessionTransitionValidating({ id: transition.id });
      }
      return;
    }

    if (hasActiveTransition) {
      return;
    }

    if (
      observedValidationRef.current ||
      sessionSnapshot.status === 'authenticated'
    ) {
      observedValidationRef.current = true;
      return;
    }

    const authTransition = beginAuthSessionTransition(
      'sign-in',
      'workspace-auth-return'
    );
    ownedTransitionRef.current = authTransition;
    observedValidationRef.current = true;
    markAuthSessionTransitionValidating(authTransition);
  }, [
    enabled,
    hasActiveTransition,
    hasSignInTransition,
    sessionSnapshot.status,
    transition?.id,
    transition?.phase,
  ]);

  useEffect(
    () => () => {
      if (ownedTransitionRef.current) {
        cancelAuthSessionTransition(ownedTransitionRef.current);
      }
    },
    []
  );

  if (!enabled) {
    return null;
  }

  const hasValidationResult =
    observedValidationRef.current ||
    hasSignInTransition ||
    sessionSnapshot.status === 'authenticated';
  const status =
    !hasValidationResult ||
    hasActiveTransition ||
    sessionSnapshot.status === 'idle' ||
    sessionSnapshot.status === 'loading'
      ? 'loading'
      : sessionSnapshot.status;

  return (
    <SiteRuntimeWorkspaceAuthReturnResolution
      onAuthenticated={onAuthenticated}
      onUnauthenticated={onUnauthenticated}
      status={status}
    />
  );
}

function SiteRuntimeWorkspaceAuthReturnResolution({
  onAuthenticated,
  onUnauthenticated,
  status,
}: {
  onAuthenticated: () => void;
  onUnauthenticated: () => void;
  status: 'loading' | 'authenticated' | 'unauthenticated';
}) {
  useEffect(() => {
    if (status === 'authenticated') {
      onAuthenticated();
    }
    if (status === 'unauthenticated') {
      onUnauthenticated();
    }
  }, [onAuthenticated, onUnauthenticated, status]);

  return null;
}

export function resolveClientEntryActionNavigationHref(params: {
  action: AdminV2EntryAction;
  locale: string;
  pagePath?: string | null;
}) {
  if (
    params.action.type !== 'switch_to_workspace' ||
    !params.action.targetToolId ||
    (params.pagePath && isWorkspaceCodeOwnedPagePath(params.pagePath))
  ) {
    return null;
  }

  return localizeSiteRuntimeHref({
    href: `/workspace?${new URLSearchParams({
      tool: params.action.targetToolId,
    })}`,
    locale: params.locale,
    external: false,
  });
}

export function createPageBoundWorkspaceLaunchDetail(params: {
  launchId: string;
  autoSubmit?: boolean;
  boundToolId: string;
  prefill?: WorkspacePrefillRequestV1;
}): CmsWorkspaceLaunchDetail {
  return {
    launchId: params.launchId,
    autoSubmit: params.autoSubmit === true,
    defaultToolId: params.boundToolId,
    forceAuthSessionRead: true,
    prefill: params.prefill,
  };
}

function normalizeLaunchDetail(params: {
  autoSubmit?: boolean;
  boundToolId: string;
  workspacePrefill?: WorkspacePrefillRequestV1;
}): CmsWorkspaceLaunchDetail {
  const base = {
    launchId: createCmsWorkspaceLaunchId(),
    autoSubmit: params.autoSubmit === true,
    defaultToolId: params.boundToolId,
    forceAuthSessionRead: true,
  };

  return createPageBoundWorkspaceLaunchDetail({
    launchId: base.launchId,
    autoSubmit: base.autoSubmit,
    boundToolId: base.defaultToolId,
    prefill: params.workspacePrefill,
  });
}

export function readRequiredRuntimeCopy(value: string, fieldPath: string) {
  const message = value.trim();
  if (!message) {
    throw new Error(`${fieldPath} is not configured.`);
  }

  return message;
}

function readFeedbackRuntimeErrorCopy(global: SiteRuntimeGlobalSnapshot) {
  return readRequiredRuntimeCopy(
    global.feedback.runtimeError,
    'Feedback runtime error copy'
  );
}

function readEntryActionNotFoundCopy(global: SiteRuntimeGlobalSnapshot) {
  return readRequiredRuntimeCopy(
    global.uiCopy.entryActionNotFound,
    'Site shell entry action not found copy'
  );
}

function readAnchorNotFoundCopy(global: SiteRuntimeGlobalSnapshot) {
  return readRequiredRuntimeCopy(
    global.uiCopy.anchorNotFound,
    'Site shell anchor not found copy'
  );
}

function readWorkspaceRuntimeLoadFailedCopy(global: SiteRuntimeGlobalSnapshot) {
  return readRequiredRuntimeCopy(
    global.workspace.errorCopy.runtimeLoadFailed,
    'Workspace runtime load failed copy'
  );
}

export function SiteRuntimeActionProvider({
  children,
  credentialCapabilities,
  global,
  locale,
  page,
  workspaceShellInitiallyAvailable = false,
}: {
  children: ReactNode;
  credentialCapabilities: CredentialRuntimeConfig;
  global: SiteRuntimeGlobalSnapshot;
  locale: string;
  page?: SiteRuntimePage | null;
  workspaceShellInitiallyAvailable?: boolean;
}) {
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  const taskBlockRetentionSuppressedRef = useRef(false);
  const workspaceToolSelectorRef = useRef<
    ((toolId: string) => Promise<boolean>) | null
  >(null);
  const pendingWorkspaceToolRef = useRef<{
    syncUrl: boolean;
    toolId: string;
  } | null>(null);
  const runPendingToolSelection = useCallback(() => {
    const pending = pendingWorkspaceToolRef.current;
    const selectTool = workspaceToolSelectorRef.current;
    if (!pending || !selectTool) return;
    pendingWorkspaceToolRef.current = null;
    void selectTool(pending.toolId)
      .then((selected) => {
        if (!selected || !pending.syncUrl) return;
        const url = new URL(window.location.href);
        url.searchParams.set('tool', pending.toolId);
        window.history.replaceState(window.history.state, '', url);
      })
      .catch(() => {
        toast.error(readFeedbackRuntimeErrorCopy(global));
      });
  }, [global]);
  const registerWorkspaceToolSelector = useCallback<
    NonNullable<WorkspaceRuntimeActionCallbacks['registerToolSelector']>
  >(
    (selectTool) => {
      workspaceToolSelectorRef.current = selectTool;
      runPendingToolSelection();
      return () => {
        if (workspaceToolSelectorRef.current === selectTool)
          workspaceToolSelectorRef.current = null;
      };
    },
    [runPendingToolSelection]
  );
  const reportRuntimeError = useCallback((message: string) => {
    toast.error(message);
  }, []);
  const [workspaceTransition, dispatchWorkspaceTransition] = useReducer(
    reduceSiteRuntimeWorkspaceTransition,
    idleSiteRuntimeWorkspaceTransition
  );
  const workspaceAuthReturnStartedRef = useRef(false);
  const workspaceAuthLoginSceneRef =
    useRef<AdminV2LoginPopupSceneSlug>('workspaceAccess');
  const activatedLaunchIdsRef = useRef(new Set<string>());
  const workspacePageIdRef = useRef(page?.pageId ?? null);
  const workspaceAuthToolId = resolveWorkspaceLaunchToolId({
    boundToolId: page?.boundToolId,
    defaultToolId: global.workspaceToolCatalog?.defaultToolId ?? null,
    isWorkspacePage: workspaceShellInitiallyAvailable,
  });
  const preloadLoginDialogRuntime = useCallback(() => {
    void loadSiteRuntimeLoginDialogRuntime().catch(() => undefined);
  }, []);
  const preloadPaywallDialogRuntime = useCallback(() => {
    void preloadSiteRuntimePaywallDialogRuntime({ locale }).catch(
      () => undefined
    );
  }, [locale]);

  useBrowserLayoutEffect(() => {
    const nextPageId = page?.pageId ?? null;
    if (workspacePageIdRef.current !== nextPageId) {
      workspacePageIdRef.current = nextPageId;
      workspaceAuthReturnStartedRef.current = false;
      activatedLaunchIdsRef.current.clear();
      dispatchWorkspaceTransition({ type: 'reset' });
    }

    if (!nextPageId) {
      return;
    }

    return subscribeCmsWorkspaceActivation(
      ({ detail }) => {
        preloadPaywallDialogRuntime();
        dispatchWorkspaceTransition({ detail, type: 'activated' });
      },
      { emitCurrent: true }
    );
  }, [page?.pageId, preloadPaywallDialogRuntime]);

  const resolvedEntryActionById = useMemo(() => {
    return {
      ...global.resolvedEntryActions,
      ...(page?.resolvedEntryActions ?? {}),
    };
  }, [global.resolvedEntryActions, page?.resolvedEntryActions]);

  useEffect(() => {
    if (typeof window.requestIdleCallback !== 'function') {
      return;
    }

    let idleRequestId: number | undefined;
    const schedulePreload = () => {
      idleRequestId = window.requestIdleCallback(() => {
        preloadLoginDialogRuntime();
        if (workspaceShellInitiallyAvailable) {
          preloadPaywallDialogRuntime();
        } else {
          void loadSiteRuntimePaywallDialogRuntime().catch(() => undefined);
        }
      });
    };

    if (document.readyState === 'complete') {
      schedulePreload();
    } else {
      window.addEventListener('load', schedulePreload, { once: true });
    }

    return () => {
      window.removeEventListener('load', schedulePreload);
      if (
        idleRequestId !== undefined &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(idleRequestId);
      }
    };
  }, [
    preloadLoginDialogRuntime,
    preloadPaywallDialogRuntime,
    workspaceShellInitiallyAvailable,
  ]);

  const getActionHref = (action: AdminV2EntryAction) => {
    if (action.type === 'open_url') {
      return localizeSiteRuntimeHref({
        href: action.href,
        locale,
        external: !isAdminV2InternalEntryActionHref(action.href),
      });
    }

    if (action.type === 'switch_to_workspace' && action.targetToolId) {
      if (
        page?.boundToolId ||
        (page && isWorkspaceCodeOwnedPagePath(page.path))
      )
        return null;
      return localizeSiteRuntimeHref({
        href: `/workspace?${new URLSearchParams({ tool: action.targetToolId })}`,
        locale,
        external: false,
      });
    }

    if (
      action.type !== 'switch_to_workspace' ||
      (page?.pageId && page.boundToolId)
    ) {
      return null;
    }

    return localizeSiteRuntimeHref({
      href: '/workspace',
      locale,
      external: false,
    });
  };

  const getTargetHref = (target: SiteRuntimeTarget) => {
    if (target.type === 'open_url') {
      return localizeSiteRuntimeHref({
        href: target.href,
        locale,
        external: target.external,
      });
    }

    if (target.type === 'client_entry_action') {
      return null;
    }

    const action =
      target.type === 'custom_entry_action'
        ? target.action
        : target.type === 'existing_entry_action'
          ? resolvedEntryActionById[target.actionId]?.action
          : null;

    return action?.type === 'switch_to_workspace'
      ? getActionHref(action)
      : null;
  };

  const preloadAction = useCallback(
    (action: AdminV2EntryAction) => {
      if (action.type === 'open_popup' && action.popupId === 'login') {
        preloadLoginDialogRuntime();
      } else if (action.type === 'open_popup' && action.popupId === 'paywall') {
        preloadPaywallDialogRuntime();
      }
    },
    [preloadLoginDialogRuntime, preloadPaywallDialogRuntime]
  );

  const preloadTarget = useCallback(
    (target: SiteRuntimeTarget) => {
      const action =
        target.type === 'custom_entry_action' ||
        target.type === 'client_entry_action'
          ? target.action
          : target.type === 'existing_entry_action'
            ? resolvedEntryActionById[target.actionId]?.action
            : null;
      if (action) {
        preloadAction(action);
      }
    },
    [preloadAction, resolvedEntryActionById]
  );

  const openLogin = useCallback((request: SiteRuntimeLoginRequest) => {
    setActiveDialog({
      kind: 'login',
      request,
    });
  }, []);

  const openDefaultLogin = () => {
    openLogin(
      page?.pageId && workspaceAuthToolId
        ? { mode: 'workspace', scene: adminV2DefaultLoginPopupSceneSlug }
        : { mode: 'plain', scene: adminV2DefaultLoginPopupSceneSlug }
    );
  };

  const beginWorkspaceAuthReturn = useCallback(
    async (callbackUrl: string, loginScene: AdminV2LoginPopupSceneSlug) => {
      if (!page?.pageId || !workspaceAuthToolId) {
        toast.error(readWorkspaceRuntimeLoadFailedCopy(global));
        return false;
      }

      const started = await issueSiteRuntimeWorkspaceAuthIntent(
        callbackUrl,
        loginScene
      );
      if (!started) {
        toast.error(readWorkspaceRuntimeLoadFailedCopy(global));
      }
      return started;
    },
    [global, page?.pageId, workspaceAuthToolId]
  );

  const cancelWorkspaceAuthReturn = useCallback(async () => {
    workspaceAuthReturnStartedRef.current = false;
    dispatchWorkspaceTransition({ type: 'auth-return-aborted' });
    await cancelSiteRuntimeWorkspaceAuthIntent();
  }, []);

  const failWorkspaceAuthReturn = useCallback(() => {
    workspaceAuthReturnStartedRef.current = false;
    clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
    dispatchWorkspaceTransition({ type: 'auth-return-failed' });
    setActiveDialog(null);
    toast.error(readWorkspaceRuntimeLoadFailedCopy(global));
    void cancelSiteRuntimeWorkspaceAuthIntent();
  }, [global]);

  const resumeWorkspaceAuthReturn = useCallback(() => {
    if (workspaceAuthReturnStartedRef.current) {
      return;
    }

    if (!page?.pageId || !workspaceAuthToolId) {
      failWorkspaceAuthReturn();
      return;
    }

    workspaceAuthReturnStartedRef.current = true;
    setActiveDialog((current) =>
      current?.kind === 'login' && current.request.mode === 'workspace'
        ? {
            ...current,
            request: {
              ...current.request,
              status: 'preparingWorkspace',
            },
          }
        : current
    );
    showSiteRuntimeWorkspaceAuthIntentDocumentHint();

    void consumeSiteRuntimeWorkspaceAuthIntent().then((intent) => {
      if (
        !intent ||
        intent.pageId !== page.pageId ||
        intent.boundToolId !== workspaceAuthToolId
      ) {
        failWorkspaceAuthReturn();
        return;
      }

      workspaceAuthLoginSceneRef.current = intent.loginScene;
      const detail = normalizeLaunchDetail({
        boundToolId: workspaceAuthToolId,
      });
      dispatchWorkspaceTransition({
        detail,
        type: 'auth-return-started',
      });
      if (workspaceShellInitiallyAvailable) {
        dispatchWorkspaceTransition({ type: 'shell-ready' });
      }
      dispatchWorkspaceTransition({ type: 'auth-intent-validated' });
    });
  }, [
    failWorkspaceAuthReturn,
    page?.pageId,
    workspaceShellInitiallyAvailable,
    workspaceAuthToolId,
  ]);

  useBrowserLayoutEffect(() => {
    if (hasSiteRuntimeWorkspaceAuthIntentDocumentHint()) {
      resumeWorkspaceAuthReturn();
    }
  }, [resumeWorkspaceAuthReturn]);

  const handleAuthenticatedWorkspaceReturn = useCallback(() => {
    dispatchWorkspaceTransition({ type: 'auth-authenticated' });
  }, []);

  const handleUnauthenticatedWorkspaceReturn = useCallback(() => {
    workspaceAuthReturnStartedRef.current = false;
    clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
    dispatchWorkspaceTransition({ type: 'auth-return-aborted' });
    void cancelSiteRuntimeWorkspaceAuthIntent();
    setActiveDialog({
      kind: 'login',
      request: {
        mode: 'workspace',
        scene: workspaceAuthLoginSceneRef.current,
        status: 'signInFailed',
      },
    });
  }, []);

  const reportWorkspaceShellReady = useCallback(() => {
    dispatchWorkspaceTransition({ type: 'shell-ready' });
  }, []);

  useEffect(() => {
    if (!isSiteRuntimeWorkspaceTransitionReady(workspaceTransition)) {
      return;
    }

    const { detail } = workspaceTransition;
    if (activatedLaunchIdsRef.current.has(detail.launchId)) {
      return;
    }
    activatedLaunchIdsRef.current.add(detail.launchId);

    workspaceAuthReturnStartedRef.current = false;
    clearSiteRuntimeWorkspaceAuthIntentDocumentHint();
    dispatchWorkspaceTransition({ detail, type: 'activated' });
    setActiveDialog(null);
    dispatchCmsWorkspaceLaunch(detail);
    scrollToWorkspaceMount();
  }, [workspaceTransition]);

  const executeAction = (
    action: AdminV2EntryAction,
    options: ActionExecutionOptions = {}
  ) => {
    if (action.type === 'switch_to_workspace') {
      if (action.targetToolId) {
        const href = getActionHref(action);
        if (href) {
          window.location.assign(href);
          return;
        }
        pendingWorkspaceToolRef.current = {
          syncUrl: false,
          toolId: action.targetToolId,
        };
        if (workspaceToolSelectorRef.current) {
          runPendingToolSelection();
        } else if (page?.boundToolId) {
          dispatchCmsWorkspaceLaunch(
            normalizeLaunchDetail({
              boundToolId: page.boundToolId,
            })
          );
        }
        scrollToWorkspaceMount();
        return;
      }
      if (!page?.pageId || !page.boundToolId) {
        const fallbackHref = getActionHref(action);
        if (fallbackHref) {
          window.location.assign(fallbackHref);
        }
        return;
      }

      const launchDetail = normalizeLaunchDetail({
        autoSubmit: action.startupInput.autoSubmit,
        boundToolId: page.boundToolId,
        workspacePrefill: options.workspacePrefill,
      });
      dispatchCmsWorkspaceLaunch(launchDetail);
      scrollToWorkspaceMount();
      return;
    }

    if (action.type === 'open_popup') {
      if (action.popupId === 'login') {
        openDefaultLogin();
        return;
      }
      if (action.popupId === 'paywall') {
        if (!action.branchSlug) {
          throw new Error(
            'Paywall popup actions must open a specific paywall branch.'
          );
        }
        openPaywallBranch(
          parseAdminV2PaywallPopupBranchSlug(action.branchSlug)
        );
        return;
      }
    }

    if (action.type === 'open_checkout') {
      let checkoutDisplay: ReturnType<
        typeof resolveSiteRuntimeCheckoutIntentDisplay
      >;
      try {
        checkoutDisplay = resolveSiteRuntimeCheckoutIntentDisplay(
          global.pricing,
          action.checkoutIntentKey
        );
      } catch {
        toast.error(
          readSiteRuntimeCheckoutErrorCopy(global.pricing, 'price_unavailable')
        );
        return;
      }

      if (checkoutDisplay.checkoutSurface === 'page') {
        window.location.assign(
          localizeSiteRuntimeHref({
            href: `/checkout/${encodeURIComponent(
              checkoutDisplay.checkoutIntentKey
            )}`,
            locale,
            external: false,
          })
        );
        return;
      }

      if (
        !checkoutDisplay.title.trim() ||
        !checkoutDisplay.buttonLabel.trim()
      ) {
        throw new Error(
          'Site runtime checkout display copy is missing after snapshot validation.'
        );
      }

      setActiveDialog({
        kind: 'checkout',
        checkoutIntentKey: checkoutDisplay.checkoutIntentKey,
        checkoutSurface: checkoutDisplay.checkoutSurface,
        planName: checkoutDisplay.title,
        buttonLabel: checkoutDisplay.buttonLabel,
      });
      return;
    }

    if (action.type === 'open_url') {
      const href = getActionHref(action);
      if (href) {
        window.location.assign(href);
      }
      return;
    }

    if (action.type === 'scroll_to_top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (action.type === 'scroll_to_anchor') {
      const target = document.getElementById(action.anchorId);
      if (!target) {
        toast.error(readAnchorNotFoundCopy(global));
        return;
      }
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    toast.error(readFeedbackRuntimeErrorCopy(global));
  };

  const executeWorkspaceAction: NonNullable<
    WorkspaceRuntimeActionCallbacks['onExecuteEntryAction']
  > = async ({ action, confirmation, context }) => {
    const analyticsProperties = {
      slot_id: context.slotId,
      action_type: action.type,
      current_tool_id: context.currentToolId,
      target_tool_id:
        action.type === 'switch_to_workspace'
          ? (action.targetToolId ?? null)
          : null,
    };
    const run = async () => {
      captureAnalyticsClientEvent(
        'workspace_action_started',
        analyticsProperties
      );
      try {
        if (action.type === 'switch_to_workspace' && action.targetToolId) {
          const mapping = action.startupInput.contextMappings?.[0];
          const primaryImage =
            mapping?.source === 'currentPrimaryInput'
              ? context.currentPrimaryInput
              : mapping?.source === 'currentPrimaryResult'
                ? context.currentPrimaryResult
                : null;
          const primaryPreviewUrl =
            mapping?.source === 'currentPrimaryInput'
              ? context.currentPrimaryInputPreview
              : mapping?.source === 'currentPrimaryResult'
                ? context.currentPrimaryResultPreview
                : null;
          const switched = await context.switchToTargetTool(
            action.targetToolId,
            {
              primaryImage,
              primaryPreviewUrl: primaryPreviewUrl ?? undefined,
              autoSubmit: action.startupInput.autoSubmit,
            }
          );
          if (!switched) {
            captureAnalyticsClientEvent('workspace_action_failed', {
              ...analyticsProperties,
              error_code: 'WORKSPACE_ACTION_TARGET_SWITCH_FAILED',
            });
            toast.error(readFeedbackRuntimeErrorCopy(global));
            return;
          }
        } else {
          executeAction(action);
        }
        captureAnalyticsClientEvent(
          'workspace_action_succeeded',
          analyticsProperties
        );
      } catch {
        captureAnalyticsClientEvent('workspace_action_failed', {
          ...analyticsProperties,
          error_code: 'WORKSPACE_ACTION_EXECUTION_FAILED',
        });
        toast.error(readFeedbackRuntimeErrorCopy(global));
        return;
      }
    };

    if (!confirmation) {
      await run();
      return;
    }

    try {
      const targetToolName =
        action.type === 'switch_to_workspace' && action.targetToolId
          ? await context.resolveTargetToolName(action.targetToolId)
          : null;
      const render = (localized: Record<string, string>) =>
        renderAdminV2WorkspaceActionConfirmationText({
          text: localized[locale] ?? localized.en,
          currentToolName: context.currentToolName,
          targetToolName,
        });
      captureAnalyticsClientEvent(
        'workspace_action_confirmation_shown',
        analyticsProperties
      );
      setActiveDialog({
        kind: 'workspace-action-confirmation',
        title: render(confirmation.title),
        description: render(confirmation.description),
        confirmLabel: render(confirmation.confirmLabel),
        cancelLabel: render(confirmation.cancelLabel),
        onConfirm: async () => {
          captureAnalyticsClientEvent(
            'workspace_action_confirmation_confirmed',
            analyticsProperties
          );
          setActiveDialog(null);
          await run();
        },
        onCancel: () => {
          captureAnalyticsClientEvent(
            'workspace_action_confirmation_cancelled',
            analyticsProperties
          );
          setActiveDialog(null);
        },
      });
    } catch {
      captureAnalyticsClientEvent('workspace_action_failed', {
        ...analyticsProperties,
        error_code: 'WORKSPACE_ACTION_CONFIRMATION_FAILED',
      });
      toast.error(readFeedbackRuntimeErrorCopy(global));
    }
  };

  const openPaywallBranch = useCallback(
    (
      branchSlug: AdminV2PaywallPopupBranchSlug,
      taskBlockPayment?: WorkspaceTaskBlockPaymentContext
    ) => {
      preloadPaywallDialogRuntime();
      if (taskBlockPayment) {
        void loadSiteRuntimeTaskPaymentDialogRuntime().catch(() => undefined);
      }
      taskBlockRetentionSuppressedRef.current = false;
      setActiveDialog({
        kind: 'paywall',
        branchSlug,
        taskBlockPayment,
      });
    },
    [preloadPaywallDialogRuntime]
  );
  const openTaskBlockPayment = useCallback(
    (taskBlockPayment: WorkspaceTaskBlockPaymentContext) => {
      setActiveDialog({ kind: 'task-payment', taskBlockPayment });
    },
    []
  );
  const suppressTaskBlockRetention = useCallback(() => {
    taskBlockRetentionSuppressedRef.current = true;
  }, []);
  const isTaskBlockRetentionSuppressed = useCallback(
    () => taskBlockRetentionSuppressedRef.current,
    []
  );
  const openUpgradeDialog = useCallback(() => {
    openPaywallBranch(
      resolveAdminV2PaywallBranchSlugFromUpgradeReason('upgrade-intent')
    );
  }, [openPaywallBranch]);

  const executeTarget = (target: SiteRuntimeTarget) => {
    if (target.type === 'none') {
      return;
    }

    if (target.type === 'open_url') {
      window.location.assign(
        localizeSiteRuntimeHref({
          href: target.href,
          locale,
          external: target.external,
        })
      );
      return;
    }

    if (target.type === 'client_entry_action') {
      const href = resolveClientEntryActionNavigationHref({
        action: target.action,
        locale,
        pagePath: page?.path,
      });
      if (href) {
        window.location.assign(href);
        return;
      }
      if (
        target.action.type === 'switch_to_workspace' &&
        target.action.targetToolId
      ) {
        pendingWorkspaceToolRef.current = {
          syncUrl: true,
          toolId: target.action.targetToolId,
        };
        if (workspaceToolSelectorRef.current) {
          runPendingToolSelection();
        }
        return;
      }
      executeAction(target.action);
      return;
    }

    if (target.type === 'custom_entry_action') {
      executeAction(target.action);
      return;
    }

    const resolved = resolvedEntryActionById[target.actionId]?.action;
    if (!resolved) {
      toast.error(readEntryActionNotFoundCopy(global));
      return;
    }
    executeAction(resolved);
  };

  const resolveButtonAction = (button: SiteRuntimePageButton) => {
    if (!page) {
      return null;
    }
    return resolveAdminV2PageButtonAction({
      button,
      defaultEntryAction: page.defaultEntryAction,
      resolvedEntryActions: page.resolvedEntryActions,
    });
  };

  const value = {
    activeDialog,
    beginWorkspaceAuthReturn,
    cancelWorkspaceAuthReturn,
    closeDialog: () => setActiveDialog(null),
    credentialCapabilities,
    executeAction,
    executeWorkspaceAction,
    executeTarget,
    getActionHref,
    getTargetHref,
    global,
    isTaskBlockRetentionSuppressed,
    locale,
    openLogin,
    openPaywallBranch,
    openTaskBlockPayment,
    page: page ?? null,
    preloadAction,
    preloadTarget,
    reportRuntimeError,
    registerWorkspaceToolSelector,
    reportWorkspaceShellReady,
    resumeWorkspaceAuthReturn,
    resolveButtonAction,
    suppressTaskBlockRetention,
    suppressWorkspaceAutoLaunch:
      workspaceTransition.phase === 'preparing-auth-return' ||
      workspaceTransition.phase === 'auth-return-failed' ||
      activeDialog?.kind === 'login' ||
      hasSiteRuntimeWorkspaceAuthIntentDocumentHint(),
    workspaceTransition,
  } satisfies SiteRuntimeActionContextValue;

  return (
    <SiteRuntimeActionContext.Provider value={value}>
      <SiteRuntimeCheckoutReturnPathTracker />
      <SiteRuntimeCheckoutLoginResumeController />
      <SiteRuntimeWorkspaceAuthReturnController
        enabled={workspaceTransition.phase === 'preparing-auth-return'}
        onAuthenticated={handleAuthenticatedWorkspaceReturn}
        onUnauthenticated={handleUnauthenticatedWorkspaceReturn}
      />
      <UpgradeDialogActionProvider onOpenUpgradeDialog={openUpgradeDialog}>
        {children}
      </UpgradeDialogActionProvider>
    </SiteRuntimeActionContext.Provider>
  );
}

export function SiteRuntimeTargetTrigger({
  children,
  className,
  dataAttributes,
  onClick,
  target,
}: {
  children: ReactNode;
  className?: string;
  dataAttributes?: SiteRuntimeElementDataAttributes;
  onClick?: () => void;
  target: SiteRuntimeTarget;
}) {
  const { executeTarget, getTargetHref, preloadTarget } =
    useSiteRuntimeActionContext();
  const href = getTargetHref(target);

  if (href) {
    const external = target.type === 'open_url' && target.external;
    return (
      <a
        {...dataAttributes}
        className={className}
        href={href}
        onClick={onClick}
        rel={getSiteRuntimeLinkRel(external)}
        target={external ? '_blank' : undefined}
      >
        {children}
      </a>
    );
  }

  if (target.type === 'none') {
    return (
      <span {...dataAttributes} className={className} onClick={onClick}>
        {children}
      </span>
    );
  }

  return (
    <button
      {...dataAttributes}
      className={className}
      onFocus={() => preloadTarget(target)}
      onClick={() => {
        executeTarget(target);
        onClick?.();
      }}
      onPointerDown={() => preloadTarget(target)}
      onPointerEnter={() => preloadTarget(target)}
      type="button"
    >
      {children}
    </button>
  );
}

export function SiteRuntimeActionTrigger({
  action,
  children,
  className,
  dataAttributes,
  onClick,
  clientNavigation = false,
}: {
  action: AdminV2EntryAction;
  children: ReactNode;
  className?: string;
  dataAttributes?: SiteRuntimeElementDataAttributes;
  onClick?: () => void;
  clientNavigation?: boolean;
}) {
  const { executeAction, getActionHref, preloadAction } =
    useSiteRuntimeActionContext();
  const href = getActionHref(action);

  if (href && !clientNavigation) {
    const external =
      action.type === 'open_url' &&
      !isAdminV2InternalEntryActionHref(action.href);
    return (
      <a
        {...dataAttributes}
        className={className}
        href={href}
        onClick={onClick}
        rel={getSiteRuntimeLinkRel(external)}
        target={external ? '_blank' : undefined}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      {...dataAttributes}
      className={className}
      onFocus={() => preloadAction(action)}
      onClick={() => {
        executeAction(action);
        onClick?.();
      }}
      onPointerDown={() => preloadAction(action)}
      onPointerEnter={() => preloadAction(action)}
      type="button"
    >
      {children}
    </button>
  );
}

export function SiteRuntimeCmsComponentButtonProvider({
  children,
  page: pageOverride,
}: {
  children: ReactNode;
  page?: SiteRuntimePage | null;
}) {
  const {
    executeAction,
    getActionHref,
    global,
    page,
    preloadAction,
    reportRuntimeError,
  } = useSiteRuntimeActionContext();
  const buttonPage = pageOverride === undefined ? page : pageOverride;

  return (
    <CmsComponentButtonActionProvider
      resolveButton={({ blockInstanceId, buttonKey, repeatedItemId }) => {
        const button = resolveAdminV2PageButtonByRuntimeIdentity({
          buttons: buttonPage?.buttons ?? [],
          blockInstanceId,
          buttonKey,
          repeatedItemId,
        });
        if (!button || !buttonPage) {
          return null;
        }
        const action = resolveAdminV2PageButtonAction({
          button,
          defaultEntryAction: buttonPage.defaultEntryAction,
          resolvedEntryActions: buttonPage.resolvedEntryActions,
        });
        return {
          external:
            action?.type === 'open_url'
              ? !isAdminV2InternalEntryActionHref(action.href)
              : false,
          href: action ? getActionHref(action) : null,
          label: button.label,
          preload: action ? () => preloadAction(action) : undefined,
          trigger: (options) => {
            if (!action) {
              return;
            }
            if (
              cmsComponentButtonInputRequiresWorkspace(button.inputKind) &&
              action.type !== 'switch_to_workspace'
            ) {
              reportRuntimeError(readWorkspaceRuntimeLoadFailedCopy(global));
              return;
            }
            if (
              button.inputKind === 'workspace_prefill' &&
              !options?.workspacePrefill
            ) {
              reportRuntimeError(readWorkspaceRuntimeLoadFailedCopy(global));
              return;
            }
            if (
              button.inputKind !== 'workspace_prefill' &&
              options?.workspacePrefill
            ) {
              reportRuntimeError(readWorkspaceRuntimeLoadFailedCopy(global));
              return;
            }
            executeAction(action, options);
          },
        };
      }}
    >
      {children}
    </CmsComponentButtonActionProvider>
  );
}

export function SiteRuntimeCheckoutIntentButton({
  checkoutIntentKey,
  children,
  className,
  dataAttributes,
}: {
  checkoutIntentKey: string;
  children: ReactNode;
  className?: string;
  dataAttributes?: SiteRuntimeElementDataAttributes;
}) {
  const { executeAction } = useSiteRuntimeActionContext();

  return (
    <button
      {...dataAttributes}
      className={className}
      onClick={() =>
        executeAction({
          type: 'open_checkout',
          checkoutIntentKey,
        })
      }
      type="button"
    >
      {children}
    </button>
  );
}

function DialogFrame({
  children,
  labelledBy,
  placement = 'modal',
}: {
  children: ReactNode;
  labelledBy: string;
  placement?: 'modal' | 'side_panel';
}) {
  const { closeDialog, global } = useSiteRuntimeActionContext();

  return (
    <div
      className={
        placement === 'side_panel'
          ? 'fixed inset-0 z-50 flex items-stretch justify-end bg-background/80 backdrop-blur-sm'
          : 'fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm'
      }
    >
      <dialog
        aria-labelledby={labelledBy}
        aria-modal="true"
        className={
          placement === 'side_panel'
            ? 'relative m-0 h-full w-full max-w-md rounded-none border-border border-l bg-background p-6 shadow-lg'
            : 'relative w-full max-w-md rounded-floating border border-border bg-background p-6 shadow-lg'
        }
        open
      >
        <div className="mb-4 flex justify-end">
          <button
            aria-label={global.uiCopy.closeDialog}
            className="inline-flex size-9 items-center justify-center rounded-control border border-border text-muted-foreground transition hover:text-foreground"
            onClick={closeDialog}
            type="button"
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        </div>
        {children}
      </dialog>
    </div>
  );
}

function DialogActionButton({
  children,
  dataAttributes,
  target,
}: {
  children: ReactNode;
  dataAttributes?: SiteRuntimeElementDataAttributes;
  target: SiteRuntimeTarget;
}) {
  return (
    <SiteRuntimeTargetTrigger
      className="mt-5 inline-flex min-h-10 items-center justify-center rounded-control bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition hover:bg-primary/90"
      dataAttributes={dataAttributes}
      target={target}
    >
      {children}
    </SiteRuntimeTargetTrigger>
  );
}

function useSiteRuntimeLoginDialogModel(scene: AdminV2LoginPopupSceneSlug) {
  const { global } = useSiteRuntimeActionContext();
  const popup = global.systemPopups.login;

  return {
    model: buildSiteRuntimeLoginDialogModel({
      popup,
      scene,
    }),
    popup,
  };
}

export function SiteRuntimeWorkspaceAuthReturnOverlay() {
  const { activeDialog } = useSiteRuntimeActionContext();
  const { model, popup } = useSiteRuntimeLoginDialogModel(
    adminV2DefaultLoginPopupSceneSlug
  );
  if (
    activeDialog?.kind === 'login' &&
    activeDialog.request.mode === 'workspace' &&
    activeDialog.request.status === 'preparingWorkspace'
  ) {
    return null;
  }
  if (!model) {
    return null;
  }

  return (
    <CmsLoginWorkspacePreparingOverlay
      model={model}
      title={popup.workspaceTransition.title}
    />
  );
}

function SiteRuntimeMarketingDialog({
  branchSlug,
  editTargets,
  popupId,
}: {
  branchSlug: string;
  editTargets?: SiteRuntimePopupPreviewEditTargets;
  popupId: string;
}) {
  const { closeDialog, global } = useSiteRuntimeActionContext();
  const popup = global.popups.find((item) => item.id === popupId);
  if (!popup) {
    return (
      <SiteRuntimeErrorToast message={readFeedbackRuntimeErrorCopy(global)} />
    );
  }
  const branch = popup.branches.find((item) => item.slug === branchSlug);
  if (!branch) {
    return (
      <SiteRuntimeErrorToast message={popup.statusCopy.branchUnavailable} />
    );
  }
  if (branch.slides.length === 0) {
    return <SiteRuntimeErrorToast message={popup.statusCopy.unavailable} />;
  }
  const firstSlide = branch.slides[0]!;

  return (
    <DialogFrame
      labelledBy={`site-runtime-popup-${popup.id}-${branch.slug}-${firstSlide.id}-title`}
    >
      <div className="grid gap-6">
        {branch.slides.map((slide, slideIndex) => (
          <section
            aria-labelledby={
              slide.title
                ? `site-runtime-popup-${popup.id}-${branch.slug}-${slide.id}-title`
                : undefined
            }
            className="border-border border-t pt-5 first:border-t-0 first:pt-0"
            key={slide.id}
          >
            {slide.title ? (
              <h2
                className="font-semibold text-2xl text-foreground tracking-normal"
                {...editTargets?.marketingSlideTitle?.(slideIndex)}
                id={`site-runtime-popup-${popup.id}-${branch.slug}-${slide.id}-title`}
              >
                {slide.title}
              </h2>
            ) : null}
            {slide.body ? (
              <p
                className="mt-3 text-muted-foreground text-sm leading-6"
                {...editTargets?.marketingSlideBody?.(slideIndex)}
              >
                {slide.body}
              </p>
            ) : null}
            {slide.action && slide.ctaLabel ? (
              <SiteRuntimeActionTrigger
                action={slide.action as AdminV2EntryAction}
                className="mt-5 inline-flex min-h-10 items-center justify-center rounded-control bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition hover:bg-primary/90"
                dataAttributes={editTargets?.marketingSlideCta?.(slideIndex)}
                onClick={closeDialog}
              >
                {slide.ctaLabel}
              </SiteRuntimeActionTrigger>
            ) : null}
          </section>
        ))}
      </div>
    </DialogFrame>
  );
}

function SiteRuntimeCheckoutDialog({
  buttonLabel,
  checkoutIntentKey,
  checkoutSurface,
  planName,
}: Extract<ActiveDialog, { kind: 'checkout' }>) {
  const { global, locale, openLogin } = useSiteRuntimeActionContext();
  const getCheckoutErrorMessage = useCallback(
    (errorCode: CheckoutErrorCode) => {
      return readSiteRuntimeCheckoutErrorCopy(global.pricing, errorCode);
    },
    [global.pricing]
  );
  const onCheckoutError = useCallback(
    (errorCode: CheckoutErrorCode) => {
      toast.error(getCheckoutErrorMessage(errorCode));
    },
    [getCheckoutErrorMessage]
  );
  const onUnauthenticated = useCallback(
    (loginCallbackUrl: string) => {
      openLogin({
        callbackUrl: loginCallbackUrl,
        mode: 'checkout',
        scene: 'checkout',
      });
    },
    [openLogin]
  );
  const checkout = useSiteRuntimeHostedCheckout({
    checkoutIntentKey,
    checkoutSurface:
      checkoutSurface === 'modal' ? 'pricing_modal' : 'pricing_side_panel',
    getCheckoutErrorMessage,
    locale,
    onCheckoutError,
    onUnauthenticated,
  });

  return (
    <DialogFrame
      labelledBy="site-runtime-checkout-dialog-title"
      placement={checkoutSurface}
    >
      <h2
        className="font-semibold text-2xl text-foreground tracking-normal"
        id="site-runtime-checkout-dialog-title"
      >
        {planName}
      </h2>
      <button
        className="mt-5 inline-flex min-h-10 items-center justify-center rounded-control bg-primary px-4 py-2 font-medium text-primary-foreground text-sm transition hover:bg-primary/90"
        disabled={checkout.submitting}
        onClick={() => {
          void checkout.startCheckout();
        }}
        type="button"
      >
        {buttonLabel}
      </button>
    </DialogFrame>
  );
}

export function SiteRuntimeErrorToast({ message }: { message: string }) {
  const { closeDialog } = useSiteRuntimeActionContext();
  useEffect(() => {
    toast.error(message);
    closeDialog();
  }, [closeDialog, message]);
  return null;
}

function SiteRuntimeLazyPopupDialog<Props extends object>({
  initialRuntime,
  load,
  props,
  runtimeKind,
  unavailableMessage,
  pendingContent,
}: {
  initialRuntime?: ComponentType<Props>;
  load: () => Promise<ComponentType<Props>>;
  props: Props;
  runtimeKind: 'login' | 'paywall' | 'task-payment';
  unavailableMessage: string;
  pendingContent?: ReactNode;
}) {
  const [RuntimeDialog, setRuntimeDialog] =
    useState<ComponentType<Props> | null>(() => initialRuntime ?? null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (RuntimeDialog) {
      return;
    }
    let isCurrent = true;
    load().then(
      (component) => {
        if (isCurrent) {
          setRuntimeDialog(() => component);
        }
      },
      (error: unknown) => {
        if (isCurrent) {
          console.error({
            event: 'SITE_RUNTIME_POPUP_RUNTIME_LOAD_FAILED',
            error,
            runtimeKind,
          });
          setLoadFailed(true);
        }
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [load, runtimeKind, RuntimeDialog]);

  if (loadFailed) {
    return <SiteRuntimeErrorToast message={unavailableMessage} />;
  }
  if (!RuntimeDialog) {
    return pendingContent ?? null;
  }
  return <RuntimeDialog {...props} />;
}

export function SiteRuntimePopupHost() {
  const { activeDialog } = useSiteRuntimeActionContext();
  if (!activeDialog) {
    return null;
  }

  return <SiteRuntimePopupDialog activeDialog={activeDialog} />;
}

function SiteRuntimePopupDialog({
  activeDialog,
  editTargets,
}: {
  activeDialog: ActiveDialog;
  editTargets?: SiteRuntimePopupPreviewEditTargets;
}) {
  const { global, locale, closeDialog } = useSiteRuntimeActionContext();

  if (activeDialog.kind === 'login') {
    const popup = global.systemPopups.login;
    return (
      <SiteRuntimeLazyPopupDialog<SiteRuntimeLoginDialogRuntimeProps>
        initialRuntime={readLoadedSiteRuntimeLoginDialogRuntime()}
        key="login"
        load={loadSiteRuntimeLoginDialogRuntime}
        props={{ editTargets, request: activeDialog.request }}
        runtimeKind="login"
        unavailableMessage={popup.statusCopy.branchUnavailable}
      />
    );
  }

  if (activeDialog.kind === 'paywall') {
    const popup = global.systemPopups.paywall;
    return (
      <SiteRuntimeLazyPopupDialog<SiteRuntimePaywallDialogRuntimeProps>
        initialRuntime={readLoadedSiteRuntimePaywallDialogRuntime()}
        key="paywall"
        load={loadSiteRuntimePaywallDialogRuntime}
        props={{
          branchSlug: activeDialog.branchSlug,
          taskBlockPayment: activeDialog.taskBlockPayment,
        }}
        runtimeKind="paywall"
        unavailableMessage={popup.statusCopy.branchUnavailable}
      />
    );
  }

  if (activeDialog.kind === 'task-payment') {
    const popup = global.systemPopups.paywall;
    return (
      <SiteRuntimeLazyPopupDialog<SiteRuntimeTaskPaymentDialogRuntimeProps>
        initialRuntime={readLoadedSiteRuntimeTaskPaymentDialogRuntime()}
        key="task-payment"
        load={loadSiteRuntimeTaskPaymentDialogRuntime}
        props={{ context: activeDialog.taskBlockPayment }}
        runtimeKind="task-payment"
        unavailableMessage={popup.statusCopy.branchUnavailable}
        pendingContent={
          <SiteRuntimeTaskPaymentDialogShell
            copy={popup.taskPayment}
            locale={locale}
            onClose={closeDialog}
          />
        }
      />
    );
  }

  if (activeDialog.kind === 'marketing') {
    return (
      <SiteRuntimeMarketingDialog
        branchSlug={activeDialog.branchSlug}
        editTargets={editTargets}
        popupId={activeDialog.popupId}
      />
    );
  }

  if (activeDialog.kind === 'workspace-action-confirmation') {
    return (
      <AlertDialog open>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{activeDialog.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {activeDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={activeDialog.onCancel}>
              {activeDialog.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void activeDialog.onConfirm();
              }}
            >
              {activeDialog.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return <SiteRuntimeCheckoutDialog {...activeDialog} />;
}

export function SiteRuntimePopupPreview({
  branchSlug,
  editTargets,
  popupId,
  popupKind,
}: {
  branchSlug: string;
  editTargets?: SiteRuntimePopupPreviewEditTargets;
  popupId: string;
  popupKind: 'login' | 'paywall' | 'marketing';
}) {
  const activeDialog: ActiveDialog =
    popupKind === 'login'
      ? {
          kind: 'login',
          request: {
            mode: 'plain',
            scene: parseAdminV2LoginPopupSceneSlug(branchSlug),
          },
        }
      : popupKind === 'paywall'
        ? {
            kind: 'paywall',
            branchSlug: parseAdminV2PaywallPopupBranchSlug(branchSlug),
          }
        : { kind: 'marketing', popupId, branchSlug };
  return (
    <SiteRuntimePopupDialog
      activeDialog={activeDialog}
      editTargets={editTargets}
    />
  );
}

function SiteRuntimePageBoundWorkspaceSection({
  launchDetail,
  pageHero,
}: {
  launchDetail: CmsWorkspaceLaunchDetail | null;
  pageHero?: SiteRuntimePageWorkspaceHero | null;
}) {
  const { global, locale, page, reportWorkspaceShellReady } =
    useSiteRuntimeActionContext();
  const dialogCallbacks = useSiteRuntimeWorkspaceDialogCallbacks();
  const actionCallbacks = useSiteRuntimeWorkspaceActionCallbacks();
  if (!page?.pageId || !page.boundToolId) {
    throw new Error(
      'Page-bound Workspace requires a published page Tool binding.'
    );
  }
  const workspaceLoadFailedCopy = readWorkspaceRuntimeLoadFailedCopy(global);

  return (
    <section
      className="scroll-mt-24"
      data-cms-workspace-anchor=""
      id="site-runtime-workspace"
    >
      <SiteRuntimePageBoundWorkspaceHost
        actionCallbacks={actionCallbacks}
        dialogCallbacks={dialogCallbacks}
        errorCopy={{
          loadFailed: workspaceLoadFailedCopy,
          retry: global.workspace.errorCopy.retryWorkspaceLoad,
        }}
        launchDetail={launchDetail}
        locale={locale}
        onShellReady={reportWorkspaceShellReady}
        pageHero={pageHero ?? undefined}
        toolId={page.boundToolId}
      />
    </section>
  );
}

export function SiteRuntimePageBoundWorkspaceSurface({
  pageHero,
}: {
  pageHero?: SiteRuntimePageWorkspaceHero | null;
} = {}) {
  const { page } = useSiteRuntimeActionContext();
  if (!page?.pageId || !page.boundToolId) {
    throw new Error(
      'Page-bound Workspace surface requires a published page Tool binding.'
    );
  }

  return (
    <SiteRuntimePageBoundWorkspaceSection
      launchDetail={createPageBoundWorkspaceLaunchDetail({
        launchId: `initial:${page.pageId}:${page.boundToolId}`,
        boundToolId: page.boundToolId,
      })}
      pageHero={pageHero}
    />
  );
}

export function SiteRuntimeWorkspaceMount({
  pageHero,
}: {
  pageHero?: SiteRuntimePageWorkspaceHero | null;
} = {}) {
  const { page, workspaceTransition } = useSiteRuntimeActionContext();
  if (!page?.pageId || !shouldMountSiteRuntimeWorkspace(workspaceTransition)) {
    return null;
  }
  const launchDetail = getSiteRuntimeWorkspaceMountDetail(workspaceTransition);

  return (
    <SiteRuntimePageBoundWorkspaceSection
      launchDetail={launchDetail}
      pageHero={pageHero}
    />
  );
}
